import React, { useState } from 'react';
import { Form, Input, Button } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthShell from '../AuthShell/AuthShell';
import {
  LockIcon, EyeIcon, EyeOffIcon, CheckIcon, AlertIcon,
  ArrowLeftIcon, TinyCheckIcon, TinyDotIcon,
} from '../AuthShell/icons';
import authService from '../../services/authService';

const RequiredLabel = ({ text }: { text: string }) => (
  <span>{text} <span className="login-required">*</span></span>
);

/** Password policy shown live under the field. */
const PASSWORD_RULES = [
  { key: 'length', label: 'At least 8 characters', test: (v: string) => v.length >= 8 },
  { key: 'upper', label: 'One uppercase letter', test: (v: string) => /[A-Z]/.test(v) },
  { key: 'lower', label: 'One lowercase letter', test: (v: string) => /[a-z]/.test(v) },
  { key: 'number', label: 'One number', test: (v: string) => /\d/.test(v) },
  { key: 'special', label: 'One special character', test: (v: string) => /[^A-Za-z0-9]/.test(v) },
];

/**
 * Step 2 of the password reset flow.
 * Reached from the mailed link: /reset-password?token=<token>
 */
const ResetPassword = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [done, setDone] = useState(false);

  const password = Form.useWatch('password', form) || '';
  const goToLogin = () => navigate('/');

  const handleSubmit = async (values: any) => {
    if (!token) return;
    setLoading(true);
    setErrorMsg('');
    try {
      await authService.resetPassword({ token, password: values.password });
      setDone(true);
    } catch (error: any) {
      setErrorMsg(error.message || 'This reset link is invalid or has expired.');
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------- Broken link ------------------------------ */
  if (!token) {
    return (
      <AuthShell tagline="Password reset">
        <div className="login-state">
          <div className="login-state-icon login-state-icon--danger"><AlertIcon /></div>
          <div className="login-state-title">This link is not valid</div>
          <div className="login-state-text">
            The reset link is incomplete or has already been used. Request a new one to continue.
          </div>
        </div>
        <Button className="login-submit-btn" type="primary" onClick={() => navigate('/forgot-password')}>
          Request a new link
        </Button>
      </AuthShell>
    );
  }

  /* ------------------------------- Success ------------------------------- */
  if (done) {
    return (
      <AuthShell tagline="Password reset">
        <div className="login-state">
          <div className="login-state-icon login-state-icon--success"><CheckIcon /></div>
          <div className="login-state-title">Password updated</div>
          <div className="login-state-text">
            Your password has been changed. Sign in with your new password to continue.
          </div>
        </div>
        <Button className="login-submit-btn" type="primary" onClick={goToLogin}>
          Back to sign in
        </Button>
      </AuthShell>
    );
  }

  /* -------------------------------- Form --------------------------------- */
  return (
    <AuthShell tagline="Set a new password for your TVS Mobility account.">
      <Form
        form={form} name="reset-password" layout="vertical" className="login-form"
        onFinish={handleSubmit} autoComplete="off"
      >
        {errorMsg ? <div className="login-alert">{errorMsg}</div> : null}

        <Form.Item
          label={<RequiredLabel text="New password" />} name="password"
          rules={[
            { required: true, message: 'New password is required' },
            {
              validator: (_, value) =>
                !value || PASSWORD_RULES.every((rule) => rule.test(value))
                  ? Promise.resolve()
                  : Promise.reject(new Error('Password does not meet the requirements below')),
            },
          ]}
        >
          <Input.Password
            prefix={<LockIcon />} placeholder="Enter your new password"
            iconRender={(visible) => (
              <span className="login-eye">{visible ? <EyeIcon /> : <EyeOffIcon />}</span>
            )}
          />
        </Form.Item>

        <ul className="login-pw-rules">
          {PASSWORD_RULES.map((rule) => {
            const passed = rule.test(password);
            return (
              <li key={rule.key} className={passed ? 'is-passed' : ''}>
                {passed ? <TinyCheckIcon /> : <TinyDotIcon />} {rule.label}
              </li>
            );
          })}
        </ul>

        <Form.Item
          label={<RequiredLabel text="Confirm password" />} name="confirm"
          dependencies={['password']}
          rules={[
            { required: true, message: 'Please re-enter the new password' },
            ({ getFieldValue }) => ({
              validator: (_, value) =>
                !value || getFieldValue('password') === value
                  ? Promise.resolve()
                  : Promise.reject(new Error('The two passwords do not match')),
            }),
          ]}
        >
          <Input.Password
            prefix={<LockIcon />} placeholder="Re-enter your new password"
            iconRender={(visible) => (
              <span className="login-eye">{visible ? <EyeIcon /> : <EyeOffIcon />}</span>
            )}
          />
        </Form.Item>

        <Button
          type="primary" htmlType="submit"
          loading={loading} className="login-submit-btn login-submit-btn--spaced"
        >
          Change password
        </Button>

        <button type="button" className="login-back-link" onClick={goToLogin}>
          <ArrowLeftIcon /> Back to sign in
        </button>
      </Form>
    </AuthShell>
  );
};

export default ResetPassword;
